<template>
  <section class="listings-page">
    <MainNavbar />
    <ToastContainer :toasts="toasts" @remove="removeToast" />

    <div class="container">
      <router-link to="/ipromiseafrica/resale/seller-dashboard" class="back-to-dashboard">
        <i class="fa-solid fa-arrow-left"></i>
        Back to Dashboard
      </router-link>
      <!-- Header remains the same as before -->
      <header class="page-header">
        <div class="header-top">
          <h1>
            <i class="fa-solid fa-store"></i>
            Your Listings
            <span class="header-badge">{{ listings.length }} items</span>
          </h1>
          <button class="add-listing-btn" @click="$router.push('/ipromiseafrica/resale/product-upload')">
            <i class="fa-solid fa-plus"></i>
            Add New
          </button>
        </div>
        
        <div class="header-filters">
          <div class="search-box">
            <i class="fa-solid fa-search"></i>
            <input v-model="search" type="search" placeholder="Search listings..." />
          </div>
          
          <div class="filter-group">
            <div class="filter-select">
              <i class="fa-solid fa-filter"></i>
              <select v-model="filterCategory">
                <option value="">All Categories</option>
                <option v-for="c in categories" :key="c" :value="c">{{ c }}</option>
              </select>
            </div>
            
            <div class="status-select">
              <i class="fa-solid fa-circle"></i>
              <select v-model="filterStatus">
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="sold">Sold</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            
            <div class="sort-select">
              <i class="fa-solid fa-sort"></i>
              <select v-model="sortBy">
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="price_high">Price: High to Low</option>
                <option value="price_low">Price: Low to High</option>
                <option value="views">Most Views</option>
                <option value="title">Title A-Z</option>
              </select>
            </div>
          </div>
          
          <div class="header-actions">
            <div class="stats-badge">
              <span class="stat-item">
                <i class="fa-solid fa-circle-check"></i>
                {{ activeListingsCount }} active
              </span>
              <span class="stat-item">
                <i class="fa-solid fa-check-circle"></i>
                {{ soldListingsCount }} sold
              </span>
            </div>
            <button class="refresh-btn" @click="fetchListings" :disabled="loading">
              <i class="fa-solid fa-arrows-rotate" :class="{ 'fa-spin': loading }"></i>
              {{ loading ? 'Loading...' : 'Refresh' }}
            </button>
          </div>
        </div>
      </header>

      <!-- Compact Stats Cards -->
      <div v-if="listings.length > 0" class="compact-stats">
        <div class="stat-item">
          <div class="stat-icon revenue">
            <i class="fa-solid fa-money-bill-wave"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">KES {{ displayRevenue.toLocaleString() }}</div>
            <div class="stat-label">Total Revenue <span class="stat-sublabel">(from sold listings)</span></div>
          </div>
        </div>
        <div class="stat-item">
          <div class="stat-icon sold">
            <i class="fa-solid fa-cart-shopping"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ soldItemsCount }}</div>
            <div class="stat-label">Items Sold</div>
          </div>
        </div>
        <div class="stat-item">
          <div class="stat-icon avg">
            <i class="fa-solid fa-chart-line"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">KES {{ averageSalePrice.toLocaleString() }}</div>
            <div class="stat-label">Avg Price</div>
          </div>
        </div>
        <div class="stat-item">
          <div class="stat-icon views">
            <i class="fa-solid fa-eye"></i>
          </div>
          <div class="stat-info">
            <div class="stat-value">{{ totalViews.toLocaleString() }}</div>
            <div class="stat-label">Total Views</div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="listings.length === 0 && !loading" class="empty-state">
        <div class="empty-illustration">
          <i class="fa-solid fa-box-open"></i>
        </div>
        <h3>No Listings Yet</h3>
        <p>Start selling by adding your first product!</p>
        <router-link to="/ipromiseafrica/resale/product-upload" class="empty-action-btn">
          <i class="fa-solid fa-plus"></i>
          Add First Product
        </router-link>
      </div>

      <!-- Compact Listings Grid -->
      <div v-if="listings.length > 0" class="listings-container">
        <div class="grid-header">
          <div class="results-count">
            Showing {{ paginatedList.length }} of {{ filtered.length }} items
          </div>
          <div class="view-options">
            <select v-model="perPage" @change="page = 1" class="per-page-select">
              <option value="12">12 per page</option>
              <option value="24">24 per page</option>
              <option value="48">48 per page</option>
            </select>
          </div>
        </div>

        <transition-group name="list" tag="div" class="compact-cards-grid">
          <article
            class="compact-listing-card"
            v-for="listing in paginatedList"
            :key="listing.listing_id"
            :class="{ 'sold': listing.is_sold, 'inactive': !listing.is_active && !listing.is_sold }"
          >
            <!-- Product Image -->
            <div class="card-image" @click="openPreview(listing)">
              <img
                :src="listing.images && listing.images.length ? listing.images[listing.coverIndex || 0] : placeholder"
                :alt="listing.title"
                class="product-image"
                loading="lazy"
                decoding="async"
              />
              
              <!-- Status Badge -->
              <div class="status-badge" :class="listing.is_sold ? 'sold' : listing.is_active ? 'active' : 'inactive'">
                {{ listing.is_sold ? 'SOLD' : listing.is_active ? 'ACTIVE' : 'INACTIVE' }}
              </div>
              
              <!-- Quick Actions (overlay): only Sell on hover to reduce clutter -->
              <div class="quick-actions">
                <button 
                  v-if="!listing.is_sold && listing.is_active" 
                  class="action-icon sell" 
                  @click.stop="openSellModal(listing)" 
                  title="Mark as Sold"
                >
                  <i class="fa-solid fa-cart-plus"></i>
                </button>
              </div>
            </div>

            <!-- Card Content -->
            <div class="card-content">
              <!-- Title and Price -->
              <div class="card-header">
                <h3 class="product-title" :title="listing.title">
                  {{ truncateText(listing.title, 50) }}
                </h3>
                <div class="product-price">
                  <span class="currency">KES</span>
                  <span class="amount">{{ Number(listing.price).toLocaleString() }}</span>
                  <span v-if="listing.sale_price" class="sold-price">
                    Sold for KES {{ (listing.sale_price * (listing.quantity_sold || 1)).toLocaleString() }}{{ (listing.quantity_sold || 0) > 1 ? ` (${listing.quantity_sold} × KES ${listing.sale_price.toLocaleString()})` : '' }}
                  </span>
                </div>
              </div>

              <!-- Meta Info -->
              <div class="card-meta">
                <span class="meta-item">
                  <i class="fa-solid fa-tag"></i>
                  {{ listing.category }}
                </span>
                <span class="meta-item">
                  <i class="fa-solid fa-star"></i>
                  {{ listing.condition }}
                </span>
                <span v-if="listing.negotiable" class="meta-item negotiable">
                  <i class="fa-solid fa-handshake"></i>
                  Negotiable
                </span>
              </div>

              <!-- Stats -->
              <div class="card-stats">
                <span class="stat">
                  <i class="fa-solid fa-eye"></i>
                  {{ listing.views || 0 }} views
                </span>
                <span class="stat">
                  <i class="fa-regular fa-clock"></i>
                  {{ formatDate(listing.created_at) }}
                </span>
              </div>

              <!-- Actions: Edit (yellow accent), Sell/Undo, Delete -->
              <div class="card-actions">
                <button class="action-btn edit" @click="openEdit(listing)" title="Edit listing">
                  <i class="fa-solid fa-pen action-icon-yellow"></i>
                  Edit
                </button>
                <button 
                  v-if="listing.is_sold" 
                  class="action-btn undo" 
                  @click="openUndoConfirm(listing)"
                >
                  <i class="fa-solid fa-rotate-left"></i>
                  Undo
                </button>
                <button 
                  v-else-if="!listing.is_sold && listing.is_active" 
                  class="action-btn sell" 
                  @click="openSellModal(listing)"
                >
                  <i class="fa-solid fa-cash-register"></i>
                  Sell
                </button>
                <button class="action-btn delete" @click="confirmDelete(listing)" title="Delete">
                  <i class="fa-solid fa-trash"></i>
                </button>
              </div>
            </div>
          </article>
        </transition-group>

        <!-- Pagination -->
        <div class="pagination" v-if="totalPages > 1">
          <button 
            @click="prevPage" 
            :disabled="page === 1" 
            class="pagination-btn prev"
          >
            <i class="fa-solid fa-chevron-left"></i>
            Previous
          </button>
          
          <div class="page-numbers">
            <button
              v-for="p in visiblePages"
              :key="p"
              @click="page = p"
              :class="{ active: page === p }"
              class="page-number"
            >
              {{ p }}
            </button>
            <span v-if="visiblePages[visiblePages.length - 1] < totalPages" class="page-dots">...</span>
          </div>
          
          <button 
            @click="nextPage" 
            :disabled="page === totalPages" 
            class="pagination-btn next"
          >
            Next
            <i class="fa-solid fa-chevron-right"></i>
          </button>
        </div>
      </div>

      <!-- UPDATED: Edit Modal with Price and Negotiable Fields -->
      <div v-if="showEdit" class="modal-overlay" @click.self="closeEdit">
        <div class="modal-content edit-modal">
          <div class="modal-header">
            <div class="modal-header-content">
              <div class="modal-icon">
                <i class="fa-solid fa-pen-to-square"></i>
              </div>
              <div>
                <h2>Edit Listing</h2>
                <p class="modal-subtitle">Update your product information</p>
              </div>
            </div>
            <button class="modal-close" @click="closeEdit">
              <i class="fa-solid fa-times"></i>
            </button>
          </div>

          <div class="modal-body">
            <form @submit.prevent="saveEdit" class="edit-form">
              <!-- Basic Information Section -->
              <div class="form-section">
                <h3 class="form-section-title">
                  <i class="fa-solid fa-circle-info"></i>
                  Basic Information
                </h3>
                <div class="form-grid">
                  <div class="form-group">
                    <label>Product Title *</label>
                    <input
                      v-model="editForm.title"
                      type="text"
                      required
                      placeholder="Enter product title"
                      class="form-input"
                    />
                  </div>
                  
                  <div class="form-group">
                    <label>Brand</label>
                    <input
                      v-model="editForm.brand"
                      type="text"
                      placeholder="e.g., Nike, Samsung"
                      class="form-input"
                    />
                  </div>
                </div>
              </div>

              <!-- Price Section -->
              <div class="form-section">
                <h3 class="form-section-title">
                  <i class="fa-solid fa-money-bill-wave"></i>
                  Pricing
                </h3>
                <div class="form-grid">
                  <div class="form-group">
                    <label>Price (KES) *</label>
                    <div class="price-input-container">
                      <span class="price-prefix">KES</span>
                      <input
                        v-model.number="editForm.price"
                        type="number"
                        min="0"
                        step="0.01"
                        required
                        placeholder="0.00"
                        class="form-input price-input"
                      />
                    </div>
                  </div>
                  
                  <div class="form-group">
                    <label>Price Negotiation</label>
                    <div class="toggle-container">
                      <label class="toggle-switch">
                        <input 
                          type="checkbox" 
                          v-model="editForm.negotiable"
                          class="toggle-checkbox"
                        />
                        <span class="toggle-slider"></span>
                        <span class="toggle-label">
                          {{ editForm.negotiable ? 'Negotiable' : 'Fixed Price' }}
                        </span>
                      </label>
                    </div>
                    <small class="form-hint">
                      Allow buyers to negotiate the price
                    </small>
                  </div>
                </div>
              </div>

              <!-- Category & Condition Section -->
              <div class="form-section">
                <h3 class="form-section-title">
                  <i class="fa-solid fa-tags"></i>
                  Category & Condition
                </h3>
                <div class="form-grid">
                  <div class="form-group">
                    <label>Category *</label>
                    <select v-model="editForm.category" required class="form-select">
                      <option value="">Select Category</option>
                      <option v-for="cat in categories" :key="cat" :value="cat">{{ cat }}</option>
                    </select>
                  </div>
                  
                  <div class="form-group">
                    <label>Condition *</label>
                    <select v-model="editForm.condition" required class="form-select">
                      <option value="">Select Condition</option>
                      <option>New</option>
                      <option>Like New</option>
                      <option>Gently Used</option>
                      <option>Worn Useable</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Description Section -->
              <div class="form-section">
                <h3 class="form-section-title">
                  <i class="fa-solid fa-file-lines"></i>
                  Description
                </h3>
                <div class="form-group">
                  <label>Short description</label>
                  <textarea
                    v-model="editForm.short_description"
                    rows="2"
                    placeholder="Brief summary for search and cards..."
                    class="form-textarea"
                  ></textarea>
                </div>
                <div class="form-group">
                  <label>Full description *</label>
                  <textarea
                    v-model="editForm.full_description"
                    rows="5"
                    placeholder="Describe your product in detail..."
                    class="form-textarea"
                    required
                  ></textarea>
                  <small class="form-hint">
                    Provide clear details about the product condition, features, and any defects
                  </small>
                </div>
              </div>

              <!-- Additional Info -->
              <div class="form-section">
                <h3 class="form-section-title">
                  <i class="fa-solid fa-circle-plus"></i>
                  Additional Information
                </h3>
                <div class="form-grid">
                  <div class="form-group">
                    <label>Product Age (months)</label>
                    <div class="age-control">
                      <button
                        type="button"
                        class="age-btn minus"
                        @click="decrementAgeMonths"
                      >
                        <i class="fa-solid fa-minus"></i>
                      </button>
                      <input
                        :value="editForm.age_months"
                        readonly
                        class="form-input age-input"
                      />
                      <button
                        type="button"
                        class="age-btn plus"
                        @click="incrementAgeMonths"
                      >
                        <i class="fa-solid fa-plus"></i>
                      </button>
                    </div>
                    <small class="form-hint">
                      Approximate age of the product in months
                    </small>
                  </div>
                </div>
              </div>

              <!-- Form Actions -->
              <div class="form-actions">
                <button type="button" class="btn btn-outline" @click="closeEdit" :disabled="savingEdit">
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="savingEdit">
                  <i class="fa-solid fa-save"></i>
                  {{ savingEdit ? 'Saving...' : 'Save Changes' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Sell info / confirmation: explains impact before opening sell form -->
      <div v-if="sellInfoModal.show" class="modal-overlay" @click.self="closeSellInfoModal">
        <div class="modal-content sell-info-modal" @click.stop role="dialog" aria-labelledby="sell-info-title">
          <div class="modal-header">
            <div class="modal-header-content">
              <div class="modal-icon info">
                <i class="fa-solid fa-circle-info"></i>
              </div>
              <div>
                <h2 id="sell-info-title">Record this sale</h2>
                <p class="modal-subtitle">This updates your sales tracking and earnings when you confirm.</p>
              </div>
            </div>
            <button class="modal-close" @click="closeSellInfoModal" aria-label="Close">
              <i class="fa-solid fa-times"></i>
            </button>
          </div>
          <div class="modal-body">
            <div class="sell-info-content">
              <p>Use this when a buyer has paid so your dashboard reflects the sale. You can undo a recorded sale from this page later if you need to.</p>
            </div>
            <div class="form-actions">
              <button type="button" class="btn btn-outline" @click="closeSellInfoModal">
                Cancel
              </button>
              <button type="button" class="btn btn-primary" @click="confirmSellInfoAndOpenForm">
                Continue to record sale
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Sell Modal (sale details form) -->
      <div v-if="sellModal.show" class="modal-overlay" @click.self="closeSellModal">
        <div class="modal-content sell-modal">
          <div class="modal-header">
            <div class="modal-header-content">
              <div class="modal-icon success">
                <i class="fa-solid fa-cash-register"></i>
              </div>
              <div>
                <h2>Mark as Sold</h2>
                <p class="modal-subtitle">Record sale details for this item</p>
              </div>
            </div>
            <button class="modal-close" @click="closeSellModal">
              <i class="fa-solid fa-times"></i>
            </button>
          </div>

          <div class="modal-body">
            <!-- Product Preview -->
            <div class="product-preview">
              <img
                :src="sellModal.listing?.images?.[sellModal.listing?.coverIndex || 0] || placeholder"
                :alt="sellModal.listing?.title"
                class="preview-image"
                loading="lazy"
                decoding="async"
              />
              <div class="preview-info">
                <h3>{{ sellModal.listing?.title }}</h3>
                <div class="preview-meta">
                  <span>Listed Price: <strong>KES {{ sellModal.listing?.price?.toLocaleString() }}</strong></span>
                </div>
              </div>
            </div>

            <!-- Sale Form -->
            <form @submit.prevent="confirmSale" class="sell-form">
              <div class="form-section">
                <h3 class="form-section-title">
                  <i class="fa-solid fa-receipt"></i>
                  Sale Details
                </h3>
                
                <!-- Sale Amount -->
                <div class="form-group">
                  <label>Sale Amount (KES) *</label>
                  <div class="price-input-container">
                    <span class="price-prefix">KES</span>
                    <input
                      v-model.number="sellModal.form.sale_price"
                      type="number"
                      min="0"
                      step="0.01"
                      required
                      :placeholder="`e.g., ${sellModal.listing?.price || '0'}`"
                      class="form-input price-input"
                    />
                  </div>
                  <small class="form-hint">
                    Enter the actual amount received for this sale
                  </small>
                </div>

                <!-- Quantity Sold -->
                <div class="form-group">
                  <label>Quantity Sold *</label>
                  <div class="quantity-input-container">
                    <button 
                      type="button" 
                      class="quantity-btn minus"
                      @click="decrementQuantity"
                      :disabled="sellModal.form.quantity_sold <= 1"
                    >
                      <i class="fa-solid fa-minus"></i>
                    </button>
                    <input
                      v-model.number="sellModal.form.quantity_sold"
                      type="number"
                      min="1"
                      required
                      class="form-input quantity-input"
                    />
                    <button 
                      type="button" 
                      class="quantity-btn plus"
                      @click="incrementQuantity"
                    >
                      <i class="fa-solid fa-plus"></i>
                    </button>
                  </div>
                  <small class="form-hint">
                    Number of items sold in this transaction
                  </small>
                </div>

                <!-- Sale Date -->
                <div class="form-group">
                  <label>Sale Date *</label>
                  <input
                    v-model="sellModal.form.sale_date"
                    type="date"
                    :max="new Date().toISOString().split('T')[0]"
                    required
                    class="form-input"
                  />
                </div>

                <!-- Notes -->
                <div class="form-group">
                  <label>Notes (Optional)</label>
                  <textarea
                    v-model="sellModal.form.notes"
                    rows="3"
                    placeholder="Any additional notes about this sale..."
                    class="form-textarea"
                  ></textarea>
                </div>

                <!-- Sale Summary -->
                <div class="sale-summary">
                  <div class="summary-item">
                    <span>Total Sale Value:</span>
                    <strong>KES {{ (sellModal.form.sale_price * sellModal.form.quantity_sold).toLocaleString() }}</strong>
                  </div>
                  <div v-if="sellModal.listing?.price" class="summary-item">
                    <span>Price Difference:</span>
                    <span :class="priceComparisonClass">
                      {{ priceDifferencePercentage }}%
                      <i :class="priceDifferenceIcon"></i>
                    </span>
                  </div>
                </div>
              </div>

              <!-- Form Actions -->
              <div class="form-actions">
                <button type="button" class="btn btn-outline" @click="closeSellModal" :disabled="selling">
                  Cancel
                </button>
                <button type="submit" class="btn btn-success" :disabled="selling">
                  <i class="fa-solid fa-check-circle"></i>
                  {{ selling ? 'Processing...' : 'Confirm Sale' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Undo Sale Confirmation Modal -->
      <div v-if="undoConfirm.show && undoConfirm.listing" class="modal-overlay" @click.self="closeUndoConfirm">
        <div class="modal-content delete-modal">
          <div class="modal-icon info">
            <i class="fa-solid fa-rotate-left"></i>
          </div>
          <h3>Undo Sale</h3>
          <p class="modal-text">
            Mark "<strong>{{ undoConfirm.listing.title }}</strong>" as available again? This will clear the sale and show the listing to buyers.
          </p>
          <div class="modal-actions">
            <button class="btn btn-outline" @click="closeUndoConfirm">Cancel</button>
            <button class="btn btn-primary" @click="undoSold(undoConfirm.listing)">
              <i class="fa-solid fa-check"></i> Undo Sale
            </button>
          </div>
        </div>
      </div>

      <!-- Delete Confirmation Modal -->
      <div v-if="deleteConfirm.show" class="modal-overlay" @click.self="deleteConfirm.show = false">
        <div class="modal-content delete-modal">
          <div class="modal-icon danger">
            <i class="fa-solid fa-trash-can"></i>
          </div>
          <h3>Delete Listing</h3>
          <p class="modal-text">
            Are you sure you want to delete "<strong>{{ deleteConfirm.title }}</strong>"?
            This action cannot be undone.
          </p>
          <div class="modal-actions">
            <button class="btn btn-outline" @click="deleteConfirm.show = false">
              Cancel
            </button>
            <button class="btn btn-danger" @click="deleteListing(deleteConfirm.listing_id)">
              <i class="fa-solid fa-trash"></i>
              Delete Permanently
            </button>
          </div>
        </div>
      </div>

      <!-- Listing detail quick-view modal -->
      <div v-if="preview.show && preview.listing" class="modal-overlay quickview-modal" @click="closePreview">
        <div class="quickview-dialog" @click.stop role="dialog" aria-modal="true" aria-labelledby="quickview-title">
          <button class="quickview-close" @click="closePreview" aria-label="Close">
            <i class="fa-solid fa-times"></i>
          </button>
          <div class="quickview-layout">
            <div class="quickview-media">
              <img
                :src="preview.listing.images && preview.listing.images.length ? preview.listing.images[preview.listing.coverIndex || 0] : placeholder"
                :alt="preview.listing.title"
                class="quickview-image"
                loading="lazy"
                decoding="async"
              />
              <div class="quickview-status" :class="preview.listing.is_sold ? 'sold' : preview.listing.is_active ? 'active' : 'inactive'">
                {{ preview.listing.is_sold ? 'SOLD' : preview.listing.is_active ? 'ACTIVE' : 'INACTIVE' }}
              </div>
            </div>
            <div class="quickview-body">
              <h2 id="quickview-title" class="quickview-title">{{ preview.listing.title }}</h2>
              <div class="quickview-price-row">
                <span class="quickview-price">KES {{ Number(preview.listing.price || 0).toLocaleString() }}</span>
                <span v-if="preview.listing.sale_price" class="quickview-sold-price">
                  Sold for KES {{ (preview.listing.sale_price * (preview.listing.quantity_sold || 1)).toLocaleString() }}{{ (preview.listing.quantity_sold || 0) > 1 ? ` (${preview.listing.quantity_sold} × KES ${preview.listing.sale_price.toLocaleString()})` : '' }}
                </span>
              </div>
              <dl class="quickview-meta">
                <div class="quickview-meta-row">
                  <dt>Category</dt>
                  <dd>{{ preview.listing.category || '—' }}</dd>
                </div>
                <div class="quickview-meta-row">
                  <dt>Condition</dt>
                  <dd>{{ preview.listing.condition || '—' }}</dd>
                </div>
                <div class="quickview-meta-row">
                  <dt>Views</dt>
                  <dd>{{ preview.listing.views || 0 }}</dd>
                </div>
                <div class="quickview-meta-row">
                  <dt>Listed</dt>
                  <dd>{{ formatDate(preview.listing.created_at) }}</dd>
                </div>
              </dl>
              <p v-if="preview.listing.short_description" class="quickview-desc">{{ preview.listing.short_description }}</p>
              <div class="quickview-actions">
                <button type="button" class="quickview-btn edit-btn" @click="closePreview(); openEdit(preview.listing)">
                  <i class="fa-solid fa-pen"></i> Edit
                </button>
                <button type="button" class="quickview-btn secondary" @click="closePreview">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <Footer />
  </section>
</template>

<script>
import MainNavbar from "../../components/main/headernfooter/header.vue";
import ToastContainer from "../../components/ui/ToastContainer.vue";
import globalLoadingMixin from "@/mixins/globalLoadingMixin";
import toastMixin from "@/mixins/toastMixin";
import { buildApiUrl } from "@/config/api";
import Footer from "./Footer.vue";

export default {
  name: "SellerListings",
  loadingMessage: 'Loading your listings...',
  mixins: [toastMixin, globalLoadingMixin],
  components: { MainNavbar, ToastContainer, Footer },

  data() {
    return {
      loading: false,
      listings: [],
      search: "",
      filterCategory: "",
      filterStatus: "",
      sortBy: "newest",
      page: 1,
      perPage: 12,
      showEdit: false,
      editForm: {},
      editingId: null,
      savingEdit: false,
      deleteConfirm: { show: false, listing_id: null, title: "" },
      preview: { show: false, listing: null },
      placeholder: "https://via.placeholder.com/300x200?text=No+Image",
      toastTimers: [],
      undoConfirm: { show: false, listing: null },
      dashboardEarnings: null,

      // Sell Modal Data
      sellModal: {
        show: false,
        listing: null,
        form: {
          sale_price: null,
          quantity_sold: 1,
          sale_date: new Date().toISOString().split('T')[0],
          notes: ""
        }
      },
      selling: false,
      sellInfoModal: { show: false, listing: null },
    };
  },

  computed: {
    categories() {
      const categories = new Set();
      this.listings.forEach(listing => {
        if (listing.category) categories.add(listing.category);
      });
      return Array.from(categories).sort();
    },

    filtered() {
      let filtered = [...this.listings];

      // Search filter
      if (this.search.trim()) {
        const query = this.search.toLowerCase();
        filtered = filtered.filter(listing =>
          (listing.title || "").toLowerCase().includes(query) ||
          (listing.brand || "").toLowerCase().includes(query) ||
          (listing.short_description || "").toLowerCase().includes(query) ||
          (listing.category || "").toLowerCase().includes(query)
        );
      }

      // Category filter
      if (this.filterCategory) {
        filtered = filtered.filter(listing => listing.category === this.filterCategory);
      }

      // Status filter
      if (this.filterStatus) {
        switch (this.filterStatus) {
          case 'active':
            filtered = filtered.filter(l => l.is_active && !l.is_sold);
            break;
          case 'sold':
            filtered = filtered.filter(l => l.is_sold);
            break;
          case 'inactive':
            filtered = filtered.filter(l => !l.is_active && !l.is_sold);
            break;
        }
      }

      // Sorting
      switch (this.sortBy) {
        case 'newest':
          filtered.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
          break;
        case 'oldest':
          filtered.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
          break;
        case 'price_high':
          filtered.sort((a, b) => (b.price || 0) - (a.price || 0));
          break;
        case 'price_low':
          filtered.sort((a, b) => (a.price || 0) - (b.price || 0));
          break;
        case 'views':
          filtered.sort((a, b) => (b.views || 0) - (a.views || 0));
          break;
        case 'title':
          filtered.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
          break;
      }

      return filtered;
    },

    totalPages() {
      return Math.max(1, Math.ceil(this.filtered.length / this.perPage));
    },

    paginatedList() {
      const start = (this.page - 1) * this.perPage;
      return this.filtered.slice(start, start + this.perPage);
    },

    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.page - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },

    // Statistics
    activeListingsCount() {
      return this.listings.filter(l => l.is_active && !l.is_sold).length;
    },

    soldListingsCount() {
      return this.listings.filter(l => l.is_sold).length;
    },

    totalRevenue() {
      return this.listings
        .filter(l => l.is_sold && l.sale_price)
        .reduce((total, listing) => total + (listing.sale_price * (listing.quantity_sold || 1)), 0);
    },
    displayRevenue() {
      return this.dashboardEarnings != null ? this.dashboardEarnings : this.totalRevenue;
    },

    soldItemsCount() {
      return this.listings
        .filter(l => l.is_sold)
        .reduce((total, listing) => total + (listing.quantity_sold || 1), 0);
    },

    averageSalePrice() {
      const soldListings = this.listings.filter(l => l.is_sold && l.sale_price);
      if (soldListings.length === 0) return 0;
      const total = soldListings.reduce((sum, l) => sum + l.sale_price, 0);
      return Math.round(total / soldListings.length);
    },

    totalViews() {
      return this.listings.reduce((total, listing) => total + (listing.views || 0), 0);
    },

    // Price comparison for sell modal
    priceDifferencePercentage() {
      if (!this.sellModal.listing?.price || !this.sellModal.form.sale_price) return 0;
      const difference = this.sellModal.form.sale_price - this.sellModal.listing.price;
      return ((difference / this.sellModal.listing.price) * 100).toFixed(1);
    },

    priceComparisonClass() {
      const percentage = this.priceDifferencePercentage;
      if (percentage > 0) return 'price-higher';
      if (percentage < 0) return 'price-lower';
      return 'price-same';
    },

    priceDifferenceIcon() {
      const percentage = this.priceDifferencePercentage;
      if (percentage > 0) return 'fa-solid fa-arrow-up';
      if (percentage < 0) return 'fa-solid fa-arrow-down';
      return 'fa-solid fa-equals';
    }
  },

  watch: {
    search() {
      this.page = 1;
    },
    filterCategory() {
      this.page = 1;
    },
    filterStatus() {
      this.page = 1;
    },
    sortBy() {
      this.page = 1;
    },
  },

  methods: {

    handleAuthError(res) {
      if (res && (res.status === 401 || res.status === 403)) {
        localStorage.removeItem("access_token");
        this.showToast("Your session has expired. Please sign in again.", "error");
        this.$router.push("/ipromiseafrica/sign-in");
        return true;
      }
      return false;
    },

    async fetchListings() {
      try {
        this.loading = true;
        const token = localStorage.getItem("access_token");

        if (!token) {
          this.showToast("You must be logged in to view your listings.", "error");
          return;
        }

        const headers = { Authorization: `Bearer ${token}` };
        const [listingsRes, statsRes] = await Promise.all([
          fetch(buildApiUrl("seller/listings/all"), { method: "GET", headers }),
          fetch(buildApiUrl("seller/dashboard-stats"), { method: "GET", headers }),
        ]);

        if (this.handleAuthError(listingsRes) || this.handleAuthError(statsRes)) return;
        if (!listingsRes.ok) throw new Error("Failed to load listings");

        const data = await listingsRes.json();
        const payload = Array.isArray(data) ? data : (data.listings || data);

        if (statsRes.ok) {
          const statsData = await statsRes.json();
          this.dashboardEarnings = statsData.earnings != null ? statsData.earnings : null;
        } else {
          this.dashboardEarnings = null;
        }

        this.listings = payload.map(listing => ({
          ...listing,
          listing_id: listing.listing_id || listing.id,
          coverIndex: listing.coverIndex || 0,
          images: listing.images || [],
          short_description: listing.short_description || listing.description || "",
          full_description: listing.full_description || listing.description || "",
          price: Number(listing.price || 0),
          negotiable: Boolean(listing.negotiable),
          condition: listing.condition || "New",
          is_active: Boolean(listing.is_active),
          is_sold: Boolean(listing.is_sold),
          views: listing.views || 0,
          sale_price: listing.sale_price || null,
          quantity_sold: listing.quantity_sold || null,
          created_at: listing.created_at || new Date().toISOString()
        }));

      } catch (error) {
        console.error("Fetch listings error:", error);
        this.showToast("Could not load your listings. Please try again.", "error");
      } finally {
        this.loading = false;
      }
    },

    nextPage() {
      if (this.page < this.totalPages) this.page++;
    },

    prevPage() {
      if (this.page > 1) this.page--;
    },

    openEdit(listing) {
      this.showEdit = true;
      this.editingId = listing.listing_id;
      this.editForm = {
        title: listing.title || "",
        brand: listing.brand || "",
        category: listing.category || "",
        short_description: listing.short_description || "",
        full_description: listing.full_description || listing.description || "",
        price: listing.price ?? 0,
        negotiable: !!listing.negotiable,
        condition: listing.condition || "New",
        age_months: (() => {
          const ageStr = listing.age || "";
          const match = ageStr.match(/(\d+)\s*month/i);
          return match ? Number(match[1]) : 0;
        })(),
      };
    },

    closeEdit() {
      this.showEdit = false;
      this.editForm = {};
      this.editingId = null;
      this.savingEdit = false;
    },

    async saveEdit() {
      try {
        this.savingEdit = true;
        const token = localStorage.getItem("access_token");

        if (!token) {
          throw new Error("Authentication required");
        }

        // Payload for backend (snake_case) – matches ListingUpdateSchema
        const payload = {
          title: this.editForm.title,
          brand: this.editForm.brand || "",
          category: this.editForm.category,
          condition: this.editForm.condition,
          short_description: this.editForm.short_description || "",
          full_description: this.editForm.full_description || "",
          price: Number(this.editForm.price),
          negotiable: Boolean(this.editForm.negotiable),
          age: this.editForm.age_months ? `${this.editForm.age_months} months` : "",
        };

        const res = await fetch(buildApiUrl(`seller/listings/${this.editingId}/update`), {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        if (this.handleAuthError(res)) return;
        if (!res.ok) {
          const error = await res.json().catch(() => ({ detail: "Update failed" }));
          const msg = Array.isArray(error.detail)
            ? error.detail.map((e) => e.msg || e.loc?.join?.(".")).join("; ")
            : (error.detail && String(error.detail)) || "Failed to update listing";
          throw new Error(msg);
        }

        const index = this.listings.findIndex((l) => l.listing_id === this.editingId);
        if (index !== -1) {
          this.listings[index] = { ...this.listings[index], ...this.editForm, negotiable: this.editForm.negotiable };
        }

        this.closeEdit();
        this.showToast("Listing updated successfully!");
      } catch (error) {
        console.error("Save edit error:", error);
        this.showToast(error.message || "Failed to save changes", "error");
      } finally {
        this.savingEdit = false;
      }
    },

    // Sell: first show info modal, then open sell form on Continue
    openSellModal(listing) {
      this.sellInfoModal.show = true;
      this.sellInfoModal.listing = { ...listing };
    },

    closeSellInfoModal() {
      this.sellInfoModal.show = false;
      this.sellInfoModal.listing = null;
    },

    confirmSellInfoAndOpenForm() {
      const listing = this.sellInfoModal.listing;
      this.closeSellInfoModal();
      if (!listing) return;
      this.sellModal.show = true;
      this.sellModal.listing = { ...listing };
      this.sellModal.form = {
        sale_price: listing.price || null,
        quantity_sold: 1,
        sale_date: new Date().toISOString().split('T')[0],
        notes: ""
      };
    },

    closeSellModal() {
      this.sellModal.show = false;
      this.sellModal.listing = null;
      this.sellModal.form = {
        sale_price: null,
        quantity_sold: 1,
        sale_date: new Date().toISOString().split('T')[0],
        notes: ""
      };
      this.selling = false;
    },

    incrementQuantity() {
      this.sellModal.form.quantity_sold++;
    },

    decrementQuantity() {
      if (this.sellModal.form.quantity_sold > 1) {
        this.sellModal.form.quantity_sold--;
      }
    },

    incrementAgeMonths() {
      if (!this.editForm.age_months && this.editForm.age_months !== 0) {
        this.$set(this.editForm, "age_months", 0);
      }
      this.editForm.age_months = Math.min(600, Number(this.editForm.age_months || 0) + 1);
    },

    decrementAgeMonths() {
      if (!this.editForm.age_months && this.editForm.age_months !== 0) {
        this.$set(this.editForm, "age_months", 0);
      }
      const current = Number(this.editForm.age_months || 0);
      this.editForm.age_months = current > 0 ? current - 1 : 0;
    },

    async confirmSale() {
      try {
        this.selling = true;
        const token = localStorage.getItem("access_token");

        if (!token) {
          throw new Error("Authentication required");
        }

        const saleData = {
          listing_id: this.sellModal.listing.listing_id,
          sale_price: this.sellModal.form.sale_price,
          quantity_sold: this.sellModal.form.quantity_sold,
          sale_date: this.sellModal.form.sale_date,
          notes: this.sellModal.form.notes,
        };

        // Update the listing to mark as sold
        const updateRes = await fetch(buildApiUrl(`seller/listings/${this.sellModal.listing.listing_id}/sell`), {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(saleData),
        });

        if (this.handleAuthError(updateRes)) return;
        if (!updateRes.ok) {
          throw new Error("Failed to mark listing as sold");
        }

        this.closeSellModal();
        this.showToast("Listing marked as sold.");
        await this.fetchListings();
      } catch (error) {
        console.error("Confirm sale error:", error);
        this.showToast(error.message || "Failed to confirm sale", "error");
      } finally {
        this.selling = false;
      }
    },

    openUndoConfirm(listing) {
      this.undoConfirm = { show: true, listing: { ...listing } };
    },

    closeUndoConfirm() {
      this.undoConfirm = { show: false, listing: null };
    },

    async undoSold(listing) {
      const target = listing || this.undoConfirm?.listing;
      if (!target) return;
      this.closeUndoConfirm();

      try {
        const token = localStorage.getItem("access_token");
        if (!token) {
          this.showToast("Authentication required", "error");
          return;
        }

        const res = await fetch(buildApiUrl(`seller/listings/${target.listing_id}/undo-sale`), {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (this.handleAuthError(res)) return;
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || "Failed to undo sale");
        }

        const index = this.listings.findIndex(l => l.listing_id === target.listing_id);
        if (index !== -1) {
          this.listings[index] = {
            ...this.listings[index],
            is_sold: false,
            sale_price: null,
            quantity_sold: null
          };
        }

        this.showToast("Sale undone. Listing is available again.");
      } catch (error) {
        console.error("Undo sale error:", error);
        this.showToast(error.message || "Failed to undo sale", "error");
      }
    },

    confirmDelete(listing) {
      this.deleteConfirm = {
        show: true,
        listing_id: listing.listing_id,
        title: listing.title,
      };
    },

    async deleteListing(listingId) {
      try {
        const token = localStorage.getItem("access_token");

        if (!token) {
          this.showToast("Authentication required", "error");
          return;
        }

        const res = await fetch(buildApiUrl(`seller/listings/${listingId}`), {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (this.handleAuthError(res)) return;
        if (!res.ok) {
          const error = await res.json().catch(() => ({ detail: "Delete failed" }));
          throw new Error(error.detail || "Failed to delete listing");
        }

        this.listings = this.listings.filter(l => l.listing_id !== listingId);
        this.deleteConfirm.show = false;
        this.showToast("Listing deleted.");
      } catch (error) {
        console.error("Delete listing error:", error);
        this.showToast(error.message || "Failed to delete listing", "error");
      }
    },

    openPreview(listing) {
      this.preview = { show: true, listing: { ...listing } };
    },

    closePreview() {
      this.preview = { show: false, listing: null };
    },

    truncateText(text, length) {
      if (!text) return "";
      if (text.length <= length) return text;
      return text.substring(0, length) + "...";
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return "Today";
      if (diffDays === 1) return "Yesterday";
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      return date.toLocaleDateString();
    },
  },

  mounted() {
    this.fetchListings();
    this._onEscape = (e) => { if (e.key === 'Escape') this.closePreview(); };
    document.addEventListener('keydown', this._onEscape);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this._onEscape);
    this.toastTimers.forEach((id) => clearInterval(id));
    this.toastTimers = [];
  },
};
</script>

<style scoped>
/* Base Styles - match App.vue theme (--background-color1, --text-color1, --border-color-hover) */
.listings-page {
  background: var(--background-color1);
  min-height: 100vh;
  color: var(--text-color1);
  font-family: "Inter", sans-serif;
}

.back-to-dashboard {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-color1);
  text-decoration: none;
  font-size: 0.95rem;
  margin-bottom: 1rem;
  padding: 0.5rem 0;
}
.back-to-dashboard:hover {
  color: var(--border-color-hover);
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 100px 24px 40px;
  padding-left: max(24px, env(safe-area-inset-left));
  padding-right: max(24px, env(safe-area-inset-right));
  padding-bottom: max(40px, env(safe-area-inset-bottom));
  box-sizing: border-box;
}

/* Enhanced Header */
.page-header {
  background: var(--background-color2);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 16px var(--box-shadow);
  border: 1px solid var(--background-color3);
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-top h1 {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-color1);
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-badge {
  background: var(--background-color3);
  color: var(--border-color-hover);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;
}

.add-listing-btn {
  background: var(--border-color-hover);
  color: var(--text-color3);
  border: none;
  padding: 10px 20px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.add-listing-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px var(--box-shadow);
}

.header-filters {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.search-box {
  position: relative;
  flex: 1;
}

.search-box i {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color2);
}

.search-box input {
  width: 100%;
  padding: 12px 20px 12px 44px;
  border-radius: 12px;
  border: 1px solid var(--background-color3);
  background: var(--background-color2);
  color: var(--text-color1);
  font-size: 15px;
  transition: all 0.2s;
}

.search-box input:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px var(--box-shadow);
}

.filter-group {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.filter-select, .status-select, .sort-select {
  position: relative;
  min-width: 180px;
}

.filter-select i, .status-select i, .sort-select i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-color2);
  font-size: 14px;
}

.filter-select select, .status-select select, .sort-select select {
  width: 100%;
  padding: 10px 16px 10px 36px;
  border-radius: 10px;
  border: 1px solid var(--background-color3);
  background: var(--background-color2);
  color: var(--text-color1);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-select select:focus, .status-select select:focus, .sort-select select:focus {
  outline: none;
  border-color: var(--border-color-hover);
}

.header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.stats-badge {
  display: flex;
  gap: 16px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-color2);
  font-size: 14px;
}

.stat-item i {
  font-size: 12px;
}

.stat-item:first-child i {
  color: var(--border-color-hover);
}

.stat-item:last-child i {
  color: var(--border-color-hover);
}

.refresh-btn {
  padding: 10px 20px;
  background: var(--background-color3);
  color: var(--border-color-hover);
  border: 1px solid var(--border-color-hover);
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.refresh-btn:hover:not(:disabled) {
  background: var(--background-color3);
  transform: translateY(-1px);
}

.refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Compact Stats - palette avoids green/aqua (amber, violet, blue, slate) */
.compact-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
  gap: 16px;
  margin-bottom: 32px;
}

.compact-stats .stat-item {
  background: var(--background-color2);
  border-radius: 14px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  border: 1px solid var(--background-color3);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.compact-stats .stat-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px var(--box-shadow);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.stat-icon.revenue {
  background: rgba(217, 119, 6, 0.15);
  color: #b45309;
}

.stat-icon.sold {
  background: rgba(124, 58, 237, 0.15);
  color: #7c3aed;
}

.stat-icon.avg {
  background: rgba(14, 165, 233, 0.15);
  color: #0ea5e9;
}

.stat-icon.views {
  background: rgba(100, 116, 139, 0.2);
  color: #64748b;
}

.stat-info {
  flex: 1;
}

.stat-value {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 13px;
  color: var(--text-color2);
}

.age-control {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.age-input {
  max-width: 80px;
  text-align: center;
}

.age-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid var(--border-color);
  background: var(--background-color2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.1s ease;
}

.age-btn i {
  font-size: 12px;
  color: var(--text-color1);
}

.age-btn:hover {
  background: var(--background-color3);
  border-color: var(--border-color-hover);
}

.age-btn:active {
  transform: scale(0.96);
}

.stat-sublabel {
  font-size: 11px;
  color: var(--text-color2);
  opacity: 0.85;
  margin-top: 2px;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: var(--background-color2);
  border-radius: 16px;
  border: 2px dashed var(--background-color3);
  margin: 40px 0;
}

.empty-illustration {
  width: 100px;
  height: 100px;
  margin: 0 auto 24px;
  background: var(--background-color3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-illustration i {
  font-size: 48px;
  color: var(--border-color-hover);
}

.empty-state h3 {
  font-size: 24px;
  margin: 0 0 16px 0;
  color: var(--text-color1);
}

.empty-state p {
  color: var(--text-color2);
  max-width: 500px;
  margin: 0 auto 32px;
  line-height: 1.6;
  font-size: 16px;
}

.empty-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  background: var(--border-color-hover);
  color: var(--text-color3);
  text-decoration: none;
  border-radius: 12px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.empty-action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px var(--box-shadow);
}

/* Loading State */
.loading-state {
  text-align: center;
  padding: 80px 20px;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 3px solid var(--loader-border);
  border-top-color: var(--loader-color);
  border-radius: 50%;
  margin: 0 auto 20px;
  animation: spin 1s linear infinite;
}

.loading-state p {
  color: var(--text-color2);
  font-size: 16px;
}

/* Compact Cards Grid */
.listings-container {
  margin-top: 32px;
}

.grid-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 0 8px;
}

.results-count {
  color: var(--text-color2);
  font-size: 14px;
}

.per-page-select {
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid var(--background-color3);
  background: var(--background-color2);
  color: var(--text-color1);
  font-size: 14px;
  cursor: pointer;
}

.compact-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
}

.compact-listing-card {
  background: var(--background-color2);
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid var(--background-color3);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.compact-listing-card:hover {
  border-color: var(--border-color-hover);
  box-shadow: 0 8px 32px var(--box-shadow);
  transform: translateY(-4px);
}

.compact-listing-card.sold {
  opacity: 0.8;
  border-color: var(--border-color-hover);
}

.compact-listing-card.inactive {
  opacity: 0.7;
  border-color: var(--background-color3);
}

/* Card Image */
.card-image {
  position: relative;
  height: 180px;
  overflow: hidden;
  cursor: pointer;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.compact-listing-card:hover .product-image {
  transform: scale(1.05);
}

.status-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.active,
.status-badge.sold {
  background: var(--border-color-hover);
  color: var(--text-color3);
}

.status-badge.inactive {
  background: var(--background-color3);
  color: var(--text-color1);
}

.quick-actions {
  position: absolute;
  bottom: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.3s ease;
}

.compact-listing-card:hover .quick-actions {
  opacity: 1;
  transform: translateY(0);
}

.action-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.2s;
}

.action-icon.sell {
  background: var(--border-color-hover);
  color: var(--text-color3);
}

.action-icon-yellow {
  color: #ca8a04;
}

.action-btn.edit {
  color: var(--text-color1);
}

.action-btn.edit .action-icon-yellow {
  color: #eab308;
}

.action-btn.edit:hover {
  border-color: rgba(234, 179, 8, 0.4);
  background: rgba(234, 179, 8, 0.08);
  color: #eab308;
}

.action-btn.edit:hover .action-icon-yellow {
  color: #facc15;
}

.action-icon:hover {
  transform: scale(1.1);
}

/* Card Content */
.card-content {
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.product-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-color1);
  line-height: 1.4;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-price {
  text-align: right;
  flex-shrink: 0;
}

.currency {
  font-size: 12px;
  color: var(--text-color2);
  margin-right: 2px;
}

.amount {
  font-size: 20px;
  font-weight: 700;
  color: var(--border-color-hover);
}

.sold-price {
  display: block;
  font-size: 11px;
  color: var(--border-color-hover);
  margin-top: 4px;
}

.card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.meta-item {
  font-size: 12px;
  color: var(--text-color2);
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: var(--background-color3);
  border-radius: 6px;
}

.meta-item.negotiable {
  background: var(--background-color3);
  color: var(--border-color-hover);
}

.card-stats {
  display: flex;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid var(--background-color3);
  font-size: 12px;
  color: var(--text-color2);
}

.stat {
  display: flex;
  align-items: center;
  gap: 4px;
}

.card-actions {
  display: flex;
  gap: 8px;
  margin-top: auto;
}

.action-btn {
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid var(--background-color3);
  background: var(--background-color2);
  color: var(--text-color1);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.2s;
}

.action-btn.undo:hover {
  background: rgba(245, 158, 11, 0.1);
  border-color: rgba(245, 158, 11, 0.3);
  color: var(--border-color-hover);
}

.action-btn.sell:hover {
  background: rgba(34, 197, 94, 0.1);
  border-color: rgba(34, 197, 94, 0.3);
  color: var(--border-color-hover);
}

.action-btn.delete {
  flex: 0;
  min-width: 40px;
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.2);
  color: #ef4444;
}

.action-btn.delete:hover {
  background: rgba(239, 68, 68, 0.2);
  transform: scale(1.05);
}

/* Pagination */
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 40px;
  padding-top: 32px;
  border-top: 1px solid var(--background-color3);
}

.pagination-btn {
  padding: 12px 20px;
  border-radius: 10px;
  border: 1px solid var(--background-color3);
  background: var(--background-color2);
  color: var(--text-color1);
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background: var(--background-color3);
  border-color: var(--box-shadow);
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 8px;
  align-items: center;
}

.page-number {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid var(--background-color3);
  background: var(--background-color2);
  color: var(--text-color1);
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.page-number:hover {
  background: var(--background-color3);
}

.page-number.active {
  background: var(--border-color-hover);
  color: var(--text-color3);
  border-color: var(--border-color-hover);
  font-weight: 600;
}

.page-dots {
  color: var(--text-color2);
  padding: 0 8px;
}

/* Enhanced Modals */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  animation: fadeIn 0.3s ease;
}

.modal-content {
  background: var(--background-color2);
  border-radius: 24px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid var(--background-color3);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.modal-header {
  padding: 28px 32px;
  border-bottom: 1px solid var(--background-color3);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.modal-header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.modal-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.modal-icon.success {
  background: linear-gradient(135deg, var(--border-color-hover), #059669);
  color: var(--text-color3);
}

.modal-icon.danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: var(--text-color3);
}

.modal-icon.info {
  background: rgba(14, 165, 233, 0.2);
  color: #0ea5e9;
}

.sell-info-modal .modal-body {
  padding: 24px 32px 32px;
}

.sell-info-content {
  margin-bottom: 24px;
}

.sell-info-content p {
  margin: 0 0 12px 0;
  color: var(--text-color1);
  font-size: 1rem;
  line-height: 1.5;
}

.sell-info-content ul {
  margin: 0 0 16px 0;
  padding-left: 24px;
  color: var(--text-color2);
  line-height: 1.6;
}

.sell-info-content li {
  margin-bottom: 8px;
}

.sell-info-content strong {
  color: var(--text-color1);
}

.sell-info-footer {
  margin-top: 16px !important;
  font-size: 0.9rem !important;
  color: var(--text-color2) !important;
}

.modal-header h2 {
  margin: 0 0 4px 0;
  font-size: 1.75rem;
  color: var(--text-color1);
}

.modal-subtitle {
  margin: 0;
  color: var(--text-color2);
  font-size: 14px;
}

.modal-close {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: none;
  background: var(--background-color3);
  color: var(--text-color1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: all 0.2s;
}

.modal-close:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: rotate(90deg);
}

.modal-body {
  padding: 32px;
}

/* Enhanced Delete Modal */
.delete-modal {
  max-width: 480px;
  text-align: center;
  padding: 48px 40px;
}

.delete-modal .modal-icon {
  margin: 0 auto 24px;
}

.delete-modal h3 {
  margin: 0 0 16px 0;
  font-size: 24px;
  color: var(--text-color1);
}

.modal-text {
  color: var(--text-color2);
  line-height: 1.6;
  margin-bottom: 32px;
  font-size: 15px;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

/* Listing detail quick-view modal */
.quickview-modal {
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left));
  animation: fadeIn 0.2s ease;
}

.quickview-dialog {
  position: relative;
  background: var(--background-color2);
  border-radius: 20px;
  width: 100%;
  max-width: 720px;
  max-height: calc(100vh - 32px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.25);
  border: 1px solid var(--background-color3);
  animation: quickviewSlide 0.3s ease;
}

@keyframes quickviewSlide {
  from { opacity: 0; transform: scale(0.96); }
  to { opacity: 1; transform: scale(1); }
}

.quickview-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--background-color3);
  border: none;
  color: var(--text-color1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: background 0.2s, transform 0.2s;
  z-index: 2;
  touch-action: manipulation;
}

.quickview-close:hover {
  background: var(--background-color3);
  transform: scale(1.05);
}

.quickview-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 0;
  max-height: calc(100vh - 32px);
}

.quickview-media {
  position: relative;
  background: var(--background-color1);
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quickview-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  max-height: 400px;
}

.quickview-status {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.quickview-status.active {
  background: rgba(100, 116, 139, 0.9);
  color: #fff;
}

.quickview-status.sold {
  background: rgba(124, 58, 237, 0.9);
  color: #fff;
}

.quickview-status.inactive {
  background: rgba(100, 116, 139, 0.6);
  color: #fff;
}

.quickview-body {
  padding: 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.quickview-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-color1);
  line-height: 1.35;
}

.quickview-price-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.quickview-price {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--text-color1);
}

.quickview-sold-price {
  font-size: 0.9rem;
  color: var(--text-color2);
}

.quickview-meta {
  display: grid;
  gap: 8px;
  margin: 0;
}

.quickview-meta-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin: 0;
}

.quickview-meta-row dt {
  margin: 0;
  font-size: 0.85rem;
  color: var(--text-color2);
  font-weight: 500;
}

.quickview-meta-row dd {
  margin: 0;
  font-size: 0.9rem;
  color: var(--text-color1);
}

.quickview-desc {
  margin: 0;
  font-size: 0.95rem;
  color: var(--text-color2);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.quickview-actions {
  display: flex;
  gap: 12px;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid var(--background-color3);
}

.quickview-btn {
  flex: 1;
  padding: 12px 20px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: transform 0.2s, box-shadow 0.2s;
  border: none;
  touch-action: manipulation;
}

.quickview-btn.edit-btn {
  background: rgba(234, 179, 8, 0.2);
  color: #ca8a04;
  border: 1px solid rgba(234, 179, 8, 0.4);
}

.quickview-btn.edit-btn:hover {
  background: rgba(234, 179, 8, 0.3);
  border-color: #eab308;
  color: #eab308;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(234, 179, 8, 0.2);
}

.quickview-btn.edit-btn i {
  color: inherit;
}

.quickview-btn.secondary {
  background: var(--background-color3);
  color: var(--text-color1);
}

.quickview-btn.secondary:hover {
  background: var(--background-color3);
  transform: translateY(-1px);
}

/* Edit Modal Styling */
.edit-modal .modal-body {
  padding: 32px;
}

.edit-form {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.form-section {
  background: rgba(255, 255, 255, 0.02);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid var(--background-color3);
}

.form-section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-color1);
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.form-section-title i {
  color: var(--border-color-hover);
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: var(--text-color2);
  font-size: 14px;
  font-weight: 500;
}

.form-input, .form-select, .form-textarea {
  width: 100%;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid var(--background-color3);
  background: var(--background-color2);
  color: var(--text-color1);
  font-size: 15px;
  transition: all 0.2s;
}

.form-input:focus, .form-select:focus, .form-textarea:focus {
  outline: none;
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px var(--background-color3);
}

.form-textarea {
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
}

.form-hint {
  display: block;
  margin-top: 6px;
  color: var(--text-color2);
  font-size: 12px;
  opacity: 0.8;
}

/* Price Input Container */
.price-input-container {
  position: relative;
  display: flex;
  align-items: center;
}

.price-prefix {
  position: absolute;
  left: 16px;
  color: var(--text-color2);
  font-size: 15px;
  font-weight: 500;
  z-index: 1;
}

.price-input {
  padding-left: 60px !important;
}

/* Toggle Switch for Negotiable Field */
.toggle-container {
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: var(--background-color2);
}

.toggle-switch {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.toggle-checkbox {
  display: none;
}

.toggle-slider {
  position: relative;
  width: 52px;
  height: 28px;
  background: var(--background-color3);
  border-radius: 34px;
  transition: all 0.3s;
  box-shadow: inset 0 0 0 1px rgba(0,0,0,0.04);
}

.toggle-slider:before {
  content: "";
  position: absolute;
  width: 22px;
  height: 22px;
  left: 3px;
  bottom: 3px;
  background: #ffffff;
  border-radius: 50%;
  transition: all 0.3s;
  box-shadow: 0 2px 4px rgba(0,0,0,0.15);
}

.toggle-checkbox:checked + .toggle-slider {
  background: var(--border-color-hover);
}

.toggle-checkbox:checked + .toggle-slider:before {
  transform: translateX(22px);
}

.toggle-label {
  font-size: 14px;
  color: var(--text-color1);
  font-weight: 500;
}

/* Sell Modal Styling */
.sell-modal .modal-body {
  padding: 32px;
}

.product-preview {
  display: flex;
  gap: 20px;
  align-items: center;
  padding: 20px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 16px;
  margin-bottom: 32px;
  border: 1px solid var(--background-color3);
}

.preview-image {
  width: 100px;
  height: 100px;
  border-radius: 12px;
  object-fit: cover;
  flex-shrink: 0;
}

.preview-info {
  flex: 1;
}

.preview-info h3 {
  margin: 0 0 8px 0;
  font-size: 18px;
  color: var(--text-color1);
  line-height: 1.4;
}

.preview-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;
  color: var(--text-color2);
}

/* Quantity Input Container */
.quantity-input-container {
  display: flex;
  align-items: center;
  gap: 10px;
}

.quantity-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid var(--background-color3);
  background: var(--background-color2);
  color: var(--text-color1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.quantity-btn:hover:not(:disabled) {
  background: var(--background-color3);
  border-color: var(--box-shadow);
}

.quantity-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.quantity-input {
  text-align: center;
  padding: 12px;
  width: 80px;
}

/* Sale Summary */
.sale-summary {
  background: rgba(0, 185, 255, 0.05);
  border-radius: 12px;
  padding: 20px;
  margin: 24px 0;
  border: 1px solid var(--background-color3);
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  font-size: 15px;
}

.summary-item:last-child {
  margin-bottom: 0;
}

.summary-item span:first-child {
  color: var(--text-color2);
}

.summary-item strong {
  font-size: 18px;
  color: var(--border-color-hover);
}

/* Price Comparison Styles */
.price-higher {
  color: var(--border-color-hover);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.price-lower {
  color: #ef4444;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.price-same {
  color: var(--text-color2);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Buttons */
.btn {
  padding: 14px 28px;
  border-radius: 12px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.3s ease;
  font-size: 15px;
}

.btn-outline {
  background: transparent;
  border: 2px solid rgba(255, 255, 255, 0.2);
  color: var(--text-color1);
}

.btn-outline:hover:not(:disabled) {
  background: var(--background-color3);
  border-color: rgba(255, 255, 255, 0.3);
}

.btn-primary {
  background: var(--border-color-hover);
  color: var(--text-color3);
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px var(--box-shadow);
}

.btn-success {
  background: linear-gradient(135deg, var(--border-color-hover), #16a34a);
  color: var(--text-color3);
}

.btn-success:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(34, 197, 94, 0.3);
}

.btn-danger {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: var(--text-color3);
}

.btn-danger:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.3);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

/* Form Actions */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  padding-top: 24px;
  margin-top: 16px;
  border-top: 1px solid var(--background-color3);
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { 
    opacity: 0;
    transform: translateY(40px) scale(0.95);
  }
  to { 
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes scaleIn {
  from { 
    opacity: 0;
    transform: scale(0.9);
  }
  to { 
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.list-move,
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

.list-leave-active {
  position: absolute;
}

/* Quick-view modal responsive */
@media (max-width: 640px) {
  .quickview-layout {
    grid-template-columns: 1fr;
    max-height: none;
  }

  .quickview-media {
    min-height: 220px;
    max-height: 280px;
  }

  .quickview-image {
    max-height: 260px;
  }

  .quickview-body {
    padding: 20px;
  }

  .quickview-title {
    font-size: 1.1rem;
  }

  .quickview-price {
    font-size: 1.35rem;
  }

  .quickview-actions {
    flex-direction: column;
  }

  .quickview-btn {
    min-height: 48px;
  }
}

/* Responsive Design */
@media (max-width: 1024px) {
  .compact-cards-grid {
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 250px), 1fr));
    gap: 20px;
  }

  .grid-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
}

@media (max-width: 768px) {
  .container {
    padding-top: 80px;
    padding-left: max(16px, env(safe-area-inset-left));
    padding-right: max(16px, env(safe-area-inset-right));
    padding-bottom: max(32px, env(safe-area-inset-bottom));
  }

  .page-header {
    padding: 20px;
    margin-bottom: 20px;
  }

  .header-top {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .header-top h1 {
    font-size: 1.5rem;
  }

  .filter-group {
    flex-direction: column;
    width: 100%;
  }

  .filter-select, .status-select, .sort-select {
    min-width: 100%;
  }

  .header-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .stats-badge {
    flex-wrap: wrap;
  }

  .refresh-btn {
    width: 100%;
    min-height: 44px;
    justify-content: center;
  }

  .compact-stats {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-bottom: 24px;
  }

  .compact-stats .stat-item {
    padding: 16px;
  }

  .stat-icon {
    width: 42px;
    height: 42px;
    font-size: 18px;
  }

  .stat-value {
    font-size: 18px;
  }

  .compact-cards-grid {
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 220px), 1fr));
    gap: 16px;
  }

  .card-content {
    padding: 16px;
  }

  .card-actions {
    flex-wrap: wrap;
  }

  .action-btn {
    min-height: 44px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .modal-content {
    max-height: 95vh;
    margin: 10px;
  }

  .modal-header {
    padding: 24px;
  }

  .modal-body {
    padding: 24px;
  }

  .product-preview {
    flex-direction: column;
    text-align: center;
  }

  .form-actions {
    flex-direction: column;
  }

  .btn {
    width: 100%;
    min-height: 48px;
    justify-content: center;
  }

  .quickview-dialog {
    max-height: calc(100vh - 24px);
    margin: auto 12px;
  }
}

@media (max-width: 480px) {
  .container {
    padding-top: 75px;
    padding-left: max(12px, env(safe-area-inset-left));
    padding-right: max(12px, env(safe-area-inset-right));
  }

  .page-header {
    padding: 16px;
    border-radius: 12px;
  }

  .header-top h1 {
    font-size: 1.3rem;
    flex-wrap: wrap;
  }

  .add-listing-btn {
    width: 100%;
    justify-content: center;
    min-height: 44px;
  }

  .compact-stats {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .compact-stats .stat-item {
    padding: 14px;
  }

  .stat-value {
    font-size: 17px;
  }

  .compact-cards-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .card-image {
    height: 200px;
  }

  .product-title {
    font-size: 15px;
  }

  .grid-header {
    flex-direction: column;
    gap: 8px;
  }

  .per-page-select {
    width: 100%;
  }

  .modal-overlay {
    padding: max(8px, env(safe-area-inset-left));
  }

  .modal-content {
    border-radius: 16px;
    margin: 8px;
  }

  .pagination {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .page-number {
    min-width: 44px;
    min-height: 44px;
  }

  .form-section {
    padding: 20px;
  }

  .modal-header {
    padding: 20px;
  }

  .modal-body {
    padding: 20px;
  }

  .quantity-input-container {
    width: 100%;
  }

  .quantity-input {
    flex: 1;
  }

  .quickview-dialog {
    margin: 8px;
    border-radius: 16px;
  }

  .quickview-close {
    top: 8px;
    right: 8px;
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .quickview-body {
    padding: 16px;
  }

  .quickview-title {
    font-size: 1rem;
  }

  .quickview-price {
    font-size: 1.25rem;
  }
}

@media (max-width: 360px) {
  .container {
    padding-left: max(10px, env(safe-area-inset-left));
    padding-right: max(10px, env(safe-area-inset-right));
  }

  .compact-stats .stat-item {
    flex-direction: column;
    text-align: center;
  }

  .card-actions .action-btn {
    flex: 1 1 100%;
  }
}
</style>